import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Navigate } from 'react-router-dom';

import { MachineActions } from 'redux/actions/settings/MachineActions';
import { MachineMaintenanceActions } from 'redux/actions/settings/MachineMaintenanceActions';
import { MachineHistoryActions } from 'redux/actions/machines/MachineHistoryActions';

import { StoreModel } from 'models/StoreModel';
import { LoginUser } from 'models/LoginUser';
import { Machine } from 'models/Machine';
import { MachineMaintenance } from 'models/MachineMaintenance';
import { MachineHistory } from 'models/MachineHistory';
import Translator from 'app/components/common/Translator';
import { MachineType } from 'models/MachineType';
import { Maintenance } from 'models/Maintenance';
import { Manufacturer } from 'models/Manufacturer';
import { MachineTypeActions } from 'redux/actions/settings/MachineTypeActions';
import { MaintenanceActions } from 'redux/actions/settings/MaintenanceActions';
import { ManufacturerActions } from 'redux/actions/settings/ManufacturerActions';
import { buttonTypes, CstmBtn } from 'app/components/common/Button';
import { ModalActions } from 'redux/actions/ModalActions';
import { ModalType } from 'models/ModalData';
import { TableFormData } from 'app/components/common/Table';

interface Props {
    fetchMachineMaintenances: typeof MachineMaintenanceActions.fetchMachineMaintenances;
    fetchMachines: typeof MachineActions.fetchMachines;
    fetchMachineHistories: typeof MachineHistoryActions.fetchMachineHistories;
    fetchMachineTypes: typeof MachineTypeActions.fetchMachineTypes;
    fetchMaintenances: typeof MaintenanceActions.fetchMaintenances;
    fetchManufacturers: typeof ManufacturerActions.fetchManufacturers;
    addManufacturer: typeof ManufacturerActions.addManufacturer,
    addMachineType: typeof MachineTypeActions.addMachineType,
    addMachine: typeof MachineActions.addMachine,
    addMaintenance: typeof MaintenanceActions.addMaintenance,
    addMachineMaintenance: typeof MachineMaintenanceActions.addMachineMaintenance,
    moveToWorkshop: typeof MachineHistoryActions.moveToWorkshop,
    addForm: typeof ModalActions.showModal,
    loginUser: LoginUser;
    machineMaintenances: MachineMaintenance[];
    machines: Machine[];
    machineHistories: MachineHistory[];
    machineTypes: MachineType[];
    maintenances: Maintenance[];
    manufacturers: Manufacturer[],
}

const startingSteps = {
    manufacturer: "Manufacturer",
    machineType: "Machine Type",
    machine: "Machine",
    maintenance: "Maintenance",
    machineMaintenance: "Machine Maintenance",
    workshop: "Go to Workshop"
}

class StartCard extends React.Component<Props> {
    componentDidMount() {
        if (this.loggedIn()) {
            if (!this.props.machineMaintenances) {
                this.props.fetchMachineMaintenances(this.props.loginUser.token);
            }
            if (!this.props.machines) {
                this.props.fetchMachines(this.props.loginUser.token);
            }
            if (!this.props.machineHistories) {
                this.props.fetchMachineHistories(this.props.loginUser.token);
            }
            if (!this.props.machineTypes) {
                this.props.fetchMachineTypes(this.props.loginUser.token);
            }
            if (!this.props.maintenances) {
                this.props.fetchMaintenances(this.props.loginUser.token);
            }
            if (!this.props.manufacturers) {
                this.props.fetchManufacturers(this.props.loginUser.token);
            }
        }
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.props.machineMaintenances && this.props.machines && this.props.machineHistories &&
            this.props.machineTypes && this.props.maintenances && this.props.manufacturers
    }

    isNewUser() {
        if (this.props.machineHistories.length === 0) {
            return true;
        }
        return false;
    }
    getNextStep() {
        if (this.props.manufacturers.length === 0) {
            return startingSteps.manufacturer;
        }
        if (this.props.machineTypes.length === 0) {
            return startingSteps.machineType
        }
        if (this.props.machines.length === 0) {
            return startingSteps.machine
        }
        if (this.props.maintenances.length === 0) {
            return startingSteps.maintenance
        }
        if (this.props.machineMaintenances.length === 0) {
            return startingSteps.machineMaintenance
        }
        if (this.props.machineHistories.length === 0) {
            return startingSteps.workshop
        }
        return "";
    }

    performNextStep() {
        let formData: TableFormData = {
            header: "",
            formObject: undefined
        };
        let addAction = undefined;
        switch (this.getNextStep()) {
            case (startingSteps.manufacturer):
                formData.header = "Add a new Manufacturer";
                formData.formObject = Manufacturer.empty();
                addAction = this.props.addManufacturer;
                break;
            case (startingSteps.machineType):
                formData.header = "Add a new Machine Type";
                formData.formObject = MachineType.empty();
                addAction = this.props.addMachineType;
                break;
            case (startingSteps.machine):
                formData.header = "Add a new Machine";
                formData.formObject = Machine.empty();
                addAction = this.props.addMachine;
                break;
            case (startingSteps.maintenance):
                formData.header = "Add a new Maintenance";
                formData.formObject = Maintenance.empty();
                addAction = this.props.addMaintenance;
                break;
            case (startingSteps.machineMaintenance):
                formData.header = "Add a new Maintenance for this Machine";
                formData.formObject = MachineMaintenance.empty();
                addAction = this.props.addMachineMaintenance;
                break;
            case (startingSteps.workshop):
                this.props.moveToWorkshop();
                return;
        }
        this.props.addForm({
            isActive: true,
            type: ModalType.FORM,
            header: formData.header,
            formObject: formData.formObject,
            callBack: addAction,
        });
    }

    render() {
        if (!this.ready()) {
            return <div className="c-page__content"></div>
        }
        const lastStep = this.getNextStep() === startingSteps.workshop;
        return (
            !this.loggedIn() ? <Navigate replace to='/account/login' /> :
                <>
                    {this.ready() && this.isNewUser() &&
                        <div className="c-card">
                            <h3 className="c-card__header">
                                <Translator text="You seem to be new here!" />
                            </h3>
                            <div className="c-card__body">
                                <p>
                                    {!lastStep &&
                                        <>
                                            <Translator text="The best next step is to create:" />
                                            <br />
                                            <CstmBtn onClick={() => this.performNextStep()} text={this.getNextStep()} buttonType={buttonTypes.add} addClass="c-button--primary" />
                                        </>
                                    }
                                    {lastStep &&
                                        <>
                                            <Translator text="Finally, add the last performed maintenance for the machine, so we can set the reminder:" />
                                            <br />
                                            <CstmBtn onClick={() => this.performNextStep()} text={this.getNextStep()} buttonType={buttonTypes.add} addClass="c-button--primary" />
                                        </>
                                    }
                                </p>
                            </div>
                        </div>
                    }
                </>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        machines: state.machines,
        machineTypes: state.machineTypes,
        maintenances: state.maintenances,
        machineMaintenances: state.machineMaintenances,
        machineHistories: state.machineHistories,
        manufacturers: state.manufacturers,
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchMachines: bindActionCreators(MachineActions.fetchMachines, dispatch),
        fetchMachineTypes: bindActionCreators(MachineTypeActions.fetchMachineTypes, dispatch),
        fetchMaintenances: bindActionCreators(MaintenanceActions.fetchMaintenances, dispatch),
        fetchMachineMaintenances: bindActionCreators(MachineMaintenanceActions.fetchMachineMaintenances, dispatch),
        fetchMachineHistories: bindActionCreators(MachineHistoryActions.fetchMachineHistories, dispatch),
        fetchManufacturers: bindActionCreators(ManufacturerActions.fetchManufacturers, dispatch),
        addManufacturer: bindActionCreators(ManufacturerActions.addManufacturer, dispatch),
        addMachineType: bindActionCreators(MachineTypeActions.addMachineType, dispatch),
        addMachine: bindActionCreators(MachineActions.addMachine, dispatch),
        addMaintenance: bindActionCreators(MaintenanceActions.addMaintenance, dispatch),
        addMachineMaintenance: bindActionCreators(MachineMaintenanceActions.addMachineMaintenance, dispatch),
        moveToWorkshop: bindActionCreators(MachineHistoryActions.moveToWorkshop, dispatch),
        addForm: bindActionCreators(ModalActions.showModal, dispatch),

    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(StartCard);